/**
 * RecommendModal - Modal to send a recommendation to a follower
 *
 * Phase 4.3 - Recommendations
 *
 * Features:
 * - Search followers to recommend to
 * - Media preview (poster, title, year)
 * - 280 char message input
 * - GOLDEN RULE: Can only recommend to people who FOLLOW YOU
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Send, User, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { effectiveTier } from '../utils/entitlements';
import { UpgradeCTA } from './ui/UpgradeCTA';
import { Z_INDEX } from '../utils/z-index';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const MESSAGE_MAX_LENGTH = 280;

interface MediaPayload {
  mediaId: number;
  mediaType: 'movie' | 'tv';
  title: string;
  poster?: string;
  year?: number;
}

interface Follower {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
}

interface RecommendModalProps {
  isOpen: boolean;
  onClose: () => void;
  media: MediaPayload;
}

export function RecommendModal({ isOpen, onClose, media }: RecommendModalProps) {
  const { user, session, profile } = useAuth();
  const isFree = !!profile && effectiveTier(profile) === 'free';
  const [followers, setFollowers] = useState<Follower[]>([]);
  const [filteredFollowers, setFilteredFollowers] = useState<Follower[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFollower, setSelectedFollower] = useState<Follower | null>(null);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch followers who follow the current user
  const fetchFollowers = useCallback(async () => {
    if (!session?.access_token || !user) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch users who follow the current user (they can receive recommendations)
      const response = await fetch(`${API_BASE_URL}/users/${user.id}/followers?limit=100`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const followersList: Follower[] = (data.users || []).map((u: Follower) => ({
        id: u.id,
        username: u.username,
        display_name: u.display_name,
        avatar_url: u.avatar_url,
      }));

      setFollowers(followersList);
      setFilteredFollowers(followersList);
    } catch (err) {
      console.error('Failed to fetch followers:', err);
      setError('Failed to load followers');
    } finally {
      setIsLoading(false);
    }
  }, [session?.access_token, user]);

  // Fetch followers when modal opens (skip for Free tier)
  useEffect(() => {
    if (isOpen && !isFree) {
      fetchFollowers();
      setSelectedFollower(null);
      setMessage('');
      setSearchQuery('');
      setSuccess(false);
      setError(null);
    }
  }, [isOpen, isFree, fetchFollowers]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Filter followers based on search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredFollowers(followers);
      return;
    }

    const query = searchQuery.toLowerCase();
    setFilteredFollowers(
      followers.filter(
        (f) =>
          f.username.toLowerCase().includes(query) ||
          (f.display_name && f.display_name.toLowerCase().includes(query))
      )
    );
  }, [searchQuery, followers]);

  // Handle send recommendation
  const handleSend = async () => {
    if (!selectedFollower || !session?.access_token) return;

    setIsSending(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/recommendations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          to_user_id: selectedFollower.id,
          media_id: media.mediaId,
          media_type: media.mediaType,
          media_title: media.title,
          media_poster: media.poster,
          media_year: media.year,
          message: message.trim() || null,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        if (data.code === 'SOCIAL_TIER_REQUIRED') {
          setError('Upgrade to Social to send recommendations');
          return;
        }
        throw new Error(data.error || `HTTP ${response.status}`);
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Failed to send recommendation:', err);
      setError(err instanceof Error ? err.message : 'Failed to send recommendation');
    } finally {
      setIsSending(false);
    }
  };

  // Handle close with Escape key
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const posterUrl = media.poster
    ? `https://image.tmdb.org/t/p/w92${media.poster}`
    : null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: Z_INDEX.modal }}
    >
      {/* Backdrop - MUST stop mousedown, mouseup, AND click per PATTERNS.md */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onClose(); }}
      />

      {/* Modal content - also stop propagation */}
      <div
        className="relative w-full max-w-md bg-[#1A1F26] rounded-xl shadow-2xl ring-1 ring-white/[0.08] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.08]">
          <h2 className="text-lg font-semibold text-white">Recommend</h2>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-white hover:bg-white/[0.08] rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Media Preview */}
        <div className="flex items-center gap-3 px-5 py-4 bg-black/20">
          {posterUrl ? (
            <img
              src={posterUrl}
              alt={media.title}
              className="w-12 h-18 object-cover rounded"
            />
          ) : (
            <div className="w-12 h-18 bg-slate-700 rounded flex items-center justify-center">
              <span className="text-xs text-slate-500">No image</span>
            </div>
          )}
          <div>
            <p className="text-sm font-medium text-white line-clamp-2">{media.title}</p>
            {media.year && (
              <p className="text-xs text-slate-400">{media.year}</p>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          {isFree ? (
            <UpgradeCTA feature="Sending recommendations" onNavigate={onClose} />
          ) : success ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 bg-teal-500/20 rounded-full flex items-center justify-center">
                <Send size={24} className="text-teal-400" />
              </div>
              <p className="text-white font-medium">Recommendation sent!</p>
              <p className="text-sm text-slate-400 mt-1">
                @{selectedFollower?.username} will be notified
              </p>
            </div>
          ) : (
            <>
              {/* Error message */}
              {error && (
                <div className="mb-4 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {/* Follower selection */}
              {!selectedFollower ? (
                <>
                  <p className="text-sm text-slate-400 mb-3">
                    Select a follower to recommend to:
                  </p>

                  {/* Search input */}
                  <div className="relative mb-3">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                      ref={inputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search followers..."
                      className="w-full pl-9 pr-4 py-2 bg-black/30 border border-white/[0.08] rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500/50"
                    />
                  </div>

                  {/* Followers list */}
                  <div className="max-h-48 overflow-y-auto rounded-lg border border-white/[0.08]">
                    {isLoading ? (
                      <div className="p-4 text-center">
                        <Loader2 size={20} className="mx-auto animate-spin text-slate-500" />
                      </div>
                    ) : filteredFollowers.length === 0 ? (
                      <div className="p-4 text-center">
                        <p className="text-sm text-slate-500">
                          {followers.length === 0
                            ? 'No one follows you yet'
                            : 'No followers match your search'}
                        </p>
                      </div>
                    ) : (
                      <ul>
                        {filteredFollowers.map((follower) => (
                          <li key={follower.id}>
                            <button
                              onClick={() => setSelectedFollower(follower)}
                              className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-white/[0.04] transition-colors"
                            >
                              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                                {follower.avatar_url ? (
                                  <img
                                    src={follower.avatar_url}
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <User size={16} className="text-slate-400" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-white truncate">
                                  {follower.display_name || follower.username}
                                </p>
                                <p className="text-xs text-slate-500 truncate">
                                  @{follower.username}
                                </p>
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Selected follower */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {selectedFollower.avatar_url ? (
                        <img
                          src={selectedFollower.avatar_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User size={20} className="text-slate-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white truncate">
                        {selectedFollower.display_name || selectedFollower.username}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        @{selectedFollower.username}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedFollower(null)}
                      className="text-xs text-teal-400 hover:text-teal-300"
                    >
                      Change
                    </button>
                  </div>

                  {/* Message input */}
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400">
                      Add a message (optional)
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX_LENGTH))}
                      placeholder="Why do you think they'd like this?"
                      rows={3}
                      className="w-full px-3 py-2 bg-black/30 border border-white/[0.08] rounded-lg text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:ring-1 focus:ring-teal-500/50"
                    />
                    <p className="text-xs text-slate-500 text-right">
                      {message.length}/{MESSAGE_MAX_LENGTH}
                    </p>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!success && !isFree && (
          <div className="flex justify-end gap-3 px-5 py-4 border-t border-white/[0.08]">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={!selectedFollower || isSending}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-500 rounded-lg hover:bg-teal-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Send size={16} />
              )}
              Send
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
